import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Dialog, Select, useSelect } from '@chilecompra/react-kit/components'
import { DOWNLOADS_MONTH_OPTIONS } from '../../config/settings/constants'
import {
  REACT_APP_DATA_CM_MASTER_URL,
  REACT_APP_TCHC_BLOB_URL,
  REACT_APP_TCMARCOH_URL
} from '../../config/settings/environment'
import generateYearsBetween from '../../modules/utils/years'
import DownloadsNavButton from '../../components/Navigation/DownloadsNavButton'
import { onGetEntityReportOptionsThunk } from './DownloadFrameworkSection.actions'
import {
  Description,
  CardDescription,
  CardTitle,
  GridContainer,
  GridItem,
  HideContainer,
  Link,
  Root,
  Text,
  Title,
  Info,
  InfoText,
  InfoIcon,
  DialogTitle,
  DialogDescription
} from './DownloadFrameworkSection.styles'
import { useEffectOnce } from '@chilecompra/react-kit'

const START_YEAR = 2016
/**
 * The DownloadFrameworkSection's container.
 */
const DownloadFrameworkSection = () => {
  const dispatch = useDispatch()
  useEffectOnce(() => {
    dispatch(onGetEntityReportOptionsThunk())
  })

  const { endYear } = useSelector(state => state.topNavbar)
  const { biddingOptions, loading: loadingOptions } = useSelector(state => state.downloadFramework)
  const [openHelpDialog, setOpenHelpDialog] = useState(false)
  const { value: frameworkValue, onChange: handleChangeFramework } = useSelect()
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear || 2022,
    overwriteCallback: () => endYear || 2022
  })
  const { value: monthValue, onChange: handleChangeMonth } = useSelect({ initialValue: '01' })
  const handleCloseHelpDialog = useCallback(() => setOpenHelpDialog(false), [])
  const handleOpenHelpDialog = useCallback(() => setOpenHelpDialog(true), [])
  const handleClickDownloadFramework = useCallback(() => {
    const anchorElement = document.createElement('a')

    anchorElement.setAttribute('download', frameworkValue)
    anchorElement.setAttribute('href', `${REACT_APP_TCHC_BLOB_URL}/maestrascm/MaestraProd_cm_${frameworkValue}.zip`)
    anchorElement.setAttribute('target', '_blank')
    anchorElement.click()
  }, [frameworkValue])
  const handleClickDownloadHistoryFramework = useCallback(() => {
    window.open(REACT_APP_TCMARCOH_URL, '_blank')
  }, [])
  const handleClickDownloadMonthTransactions = useCallback(() => {
    const parsedMonthValue = Number.parseInt(monthValue, 10)
    const anchorElement = document.createElement('a')

    anchorElement.setAttribute('download', `${yearValue}${monthValue}`)
    anchorElement.setAttribute('href', `${REACT_APP_TCHC_BLOB_URL}/planillas-cm/${yearValue}-${parsedMonthValue}.zip`)
    anchorElement.setAttribute('target', '_blank')
    anchorElement.click()
  }, [monthValue, yearValue])
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, START_YEAR), [endYear])
  const isCurrentYear = useMemo(() => yearValue === new Date().getFullYear(), [yearValue])
  const monthOptions = useMemo(
    () =>
      isCurrentYear
        ? DOWNLOADS_MONTH_OPTIONS.filter((_, index) => index <= new Date().getMonth())
        : DOWNLOADS_MONTH_OPTIONS,
    [isCurrentYear]
  )

  return (
    <Root>
      <GridContainer padding="0 0 26px">
        <DownloadsNavButton />
        <GridItem xs={10} sm={11}>
          <Title>Productos y servicios en Convenio Marco</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Description>
            Descarga el listado de productos y servicios disponibles en cada uno de los Convenios Marco que se
            encuentran habilitados en la tienda. Estos reportes corresponden a archivos en formato .csv y son
            actualizados semanalmente los días martes.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 9px">
        <GridItem xs={12}>
          <Select
            label="CONVENIO MARCO"
            loading={loadingOptions}
            options={biddingOptions}
            value={frameworkValue}
            onChange={handleChangeFramework}
          />
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 25px">
        <GridItem align="right" xs={12}>
          <Button color="primary" onClick={handleClickDownloadFramework}>
            Descargar archivo
          </Button>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 45px">
        <GridItem xs={12}>
          <Text>
            Si necesitas acceder a las URLs de cada Convenio Marco puedes descargar la{' '}
            <Link href={REACT_APP_DATA_CM_MASTER_URL}>Maestra de productos y servicios</Link>
          </Text>
        </GridItem>
      </GridContainer>
      <HideContainer>
        <GridContainer padding="0 0 46px">
          <GridItem xs={12}>
            <Card color="blank" elevation="1">
              <GridContainer padding="0 0 15px">
                <GridItem xs={12}>
                  <CardTitle>Ofertas realizadas en los convenios marco</CardTitle>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 25px">
                <GridItem xs={12}>
                  <CardDescription>
                    Descarga el registro detallado de todas las ofertas que han sido realizadas en cada uno de los
                    Convenios Marcos el día de hoy (28/01/2022)
                  </CardDescription>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem align="right" xs={12}>
                  <Button color="primary" onClick={handleClickDownloadHistoryFramework}>
                    Descargar archivo
                  </Button>
                </GridItem>
              </GridContainer>
            </Card>
          </GridItem>
        </GridContainer>
      </HideContainer>
      <GridContainer>
        <GridItem xs={12}>
          <Card color="blank" elevation="1">
            <GridContainer padding="0 0 15px">
              <GridItem xs={12}>
                <CardTitle>Transacciones mensuales</CardTitle>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 22px">
              <GridItem xs={12}>
                <CardDescription>
                  Descarga el historial de transacciones realizadas a través de la tienda de Convenio Marco. Estos datos
                  son extraídos desde Mercado Público para ser procesados y depurados, recuerda que los montos transados
                  provienen de órdenes de compra, las cuales constituyen un compromiso, no un pago.
                </CardDescription>
              </GridItem>
            </GridContainer>
            <GridContainer alignItems="center" padding="0" spacing={2}>
              <GridItem xs={12} md={4}>
                <Select label="AÑO" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
              </GridItem>
              <GridItem xs={12} md={4}>
                <Select label="MES" options={monthOptions} value={monthValue} onChange={handleChangeMonth} />
              </GridItem>
              <GridItem xs={12} md={4} />
            </GridContainer>
            <GridContainer>
              <GridItem xs={12} md={8}>
                <Info onClick={handleOpenHelpDialog}>
                  <InfoIcon />
                  <InfoText>Conoce como se procesan los datos</InfoText>
                </Info>
              </GridItem>
              <GridItem align="right" xs={12} md={4}>
                <Button color="primary" onClick={handleClickDownloadMonthTransactions}>
                  Descargar archivo
                </Button>
              </GridItem>
            </GridContainer>
          </Card>
        </GridItem>
      </GridContainer>
      <Dialog maxWidth="800px" open={openHelpDialog} onClose={handleCloseHelpDialog}>
        <GridContainer padding="32px 4px 16px">
          <GridItem xs={12}>
            <DialogTitle>Procesamiento de datos</DialogTitle>
          </GridItem>
        </GridContainer>
        <GridContainer padding="0 4px 20px">
          <GridItem xs={12}>
            <DialogDescription>
              Los datos son extraídos mensualmente desde la base de datos de Mercado Público, para luego ser
              transformados a una moneda común y proceder a eliminar todas las transacciones que presenten outlier
              evidente en el monto asociado a la orden de compra.
              <br />
              <br />
              *Un outlier es un valor desproporcionado con respecto a un parámetro, como puede ser la historia de las
              órdenes de compra de un organismo, el tamaño de un organismo u otros.
            </DialogDescription>
          </GridItem>
        </GridContainer>
        <GridContainer padding="0 4px">
          <GridItem align="center" padding="28px 0" xs={12}>
            <Button color="default" onClick={handleCloseHelpDialog}>
              Cerrar
            </Button>
          </GridItem>
        </GridContainer>
      </Dialog>
    </Root>
  )
}

export default DownloadFrameworkSection
